import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../api';
import { PeopleContext } from '../../context/PeopleContext';
import TempPasswordNotice from './TempPasswordNotice';

const DEFAULT_DEPARTMENTS = ['Live', 'Retail', 'Envy', 'MPG Live'];

// "Ana Katarina Frković" -> "ana.katarina.frkovic" (valid username: letters, digits, . _ -; max 40).
export function suggestUsername(name) {
  return (name || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[đĐ]/g, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 40)
    .replace(/\.+$/g, '');
}

// Requested-by people. Deactivate instead of delete: historical entries reference the name as text.
export default function PeopleSection({ loggedInUser }) {
  const { fetchPeople } = useContext(PeopleContext);
  const [people, setPeople] = useState([]);
  const [name, setName] = useState('');
  const [department, setDepartment] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDept, setEditDept] = useState('');
  const [error, setError] = useState('');
  // Access (requester login) for an existing person — the person IS the requester, no new individual.
  const [accessFor, setAccessFor] = useState(null); // { id, username }
  const [issued, setIssued] = useState(null); // { username, temporaryPassword }

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/people`);
      setPeople(res.data);
    } catch { toast.error('Could not load people.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const departments = [...new Set([...DEFAULT_DEPARTMENTS, ...people.map((p) => p.department).filter(Boolean)])];

  const write = async (fn) => {
    setError('');
    try {
      await fn();
      await load();
      if (fetchPeople) fetchPeople(); // refresh dropdowns elsewhere
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save.');
      return false;
    }
  };

  const add = async () => {
    if (await write(() => axios.post(`${API_BASE_URL}/api/people`, { username: loggedInUser, name, department }))) {
      setName(''); setDepartment('');
    }
  };
  const saveEdit = async (id) => {
    if (await write(() => axios.put(`${API_BASE_URL}/api/people/${id}`, { username: loggedInUser, name: editName, department: editDept }))) {
      setEditId(null);
    }
  };
  const grantAccess = async () => {
    if (!accessFor) return;
    const ok = await write(async () => {
      const res = await axios.post(`${API_BASE_URL}/api/users`, { role: 'requester', person_id: accessFor.id, username: accessFor.username });
      setIssued({ username: res.data.username, temporaryPassword: res.data.temporaryPassword });
    });
    if (ok) setAccessFor(null);
  };
  const resetAccess = async (person) => {
    if (!window.confirm(`Reset the password for ${person.account_username}? The old password stops working.`)) return;
    await write(async () => {
      const res = await axios.post(`${API_BASE_URL}/api/users/${person.account_user_id}/reset-password`);
      setIssued({ username: res.data.username, temporaryPassword: res.data.temporaryPassword });
    });
  };
  const toggleActive = (person) => write(() =>
    axios.put(`${API_BASE_URL}/api/people/${person.id}`, { username: loggedInUser, is_active: person.is_active ? 0 : 1 }));

  const input = 'border p-2 rounded text-black bg-white dark:bg-black dark:text-white';
  return (
    <div>
      <div className="flex gap-2 mb-2 max-w-2xl">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className={`${input} flex-1`} />
        <select value={department} onChange={(e) => setDepartment(e.target.value)} className={`${input} flex-1`}>
          <option value="">Department</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <button onClick={add} className="bg-indigo-500 text-white px-4 py-2 rounded">Add</button>
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">{error}</p>}
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 max-w-2xl">
        Renaming does not change historical entries (they keep the name from when they were logged). Two people with the same name: add the department in brackets, e.g. “Ana (Retail)”.
      </p>
      <TempPasswordNotice issued={issued} onClose={() => setIssued(null)} />
      <ul className="max-w-3xl">
        {people.map((p) => (
          <li key={p.id} className={`flex justify-between items-center border-b p-2 ${p.is_active ? '' : 'opacity-60'}`}>
            {editId === p.id ? (
              <>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} className={`${input} mr-2 flex-1`} />
                <select value={editDept} onChange={(e) => setEditDept(e.target.value)} className={`${input} mr-2`}>
                  {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <button onClick={() => saveEdit(p.id)} className="bg-green-500 text-white px-2 py-1 rounded mr-1">Save</button>
                <button onClick={() => setEditId(null)} className="bg-gray-300 text-black px-2 py-1 rounded">Cancel</button>
              </>
            ) : (
              <>
                <span>
                  {p.name} – {p.department}{!p.is_active && <em className="ml-2 text-xs">(inactive)</em>}
                  {p.account_username && (
                    <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded" title="This person has access to the requester portal">
                      pristup: {p.account_username}
                    </span>
                  )}
                </span>
                <span className="whitespace-nowrap">
                  {p.account_username ? (
                    <button onClick={() => resetAccess(p)} className="bg-gray-700 text-white px-2 py-1 rounded mr-1">Reset password</button>
                  ) : p.is_active && accessFor?.id === p.id ? (
                    <span className="mr-1">
                      <input value={accessFor.username} aria-label={`Username for ${p.name}`}
                        onChange={(e) => setAccessFor({ ...accessFor, username: e.target.value })} className={`${input} p-1 w-44 mr-1`} />
                      <button onClick={grantAccess} className="bg-green-600 text-white px-2 py-1 rounded mr-1">Create</button>
                      <button onClick={() => setAccessFor(null)} className="bg-gray-300 text-black px-2 py-1 rounded mr-1">Cancel</button>
                    </span>
                  ) : p.is_active ? (
                    <button onClick={() => setAccessFor({ id: p.id, username: suggestUsername(p.name) })}
                      className="bg-teal-600 text-white px-2 py-1 rounded mr-1">Grant access</button>
                  ) : null}
                  <button onClick={() => { setEditId(p.id); setEditName(p.name); setEditDept(p.department || ''); }}
                    className="bg-yellow-500 text-white px-2 py-1 rounded mr-1">Edit</button>
                  <button onClick={() => toggleActive(p)} className="bg-gray-500 text-white px-2 py-1 rounded">
                    {p.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
